import { getUserId } from "src/config";
import firestore from "src/apiService/firestore";
import type {
  GeneralLedgerAccountV2,
  GeneralLedgerAccountV2Type,
} from "src/interfaces/generalLedgerAccount.interface";
import { DefaultAccounts } from "src/utils/general-ledger-accounts";

const db = firestore();

function normalizeAccountName(name: string): string {
  return name.toLowerCase().trim().replace(/\s+/g, "");
}

const reservedDefaultAccountNames = new Set(
  DefaultAccounts.map((account) => normalizeAccountName(account.name))
);

export async function generalLedgerAccountExists(
  accountName: string
): Promise<boolean> {
  const normalizedName = normalizeAccountName(accountName);
  if (!normalizedName) return false;
  if (reservedDefaultAccountNames.has(normalizedName)) return true;

  const userId = await getUserId();
  const existingAccounts = await db
    .collection("GL_Accounts")
    .where("user", "==", userId)
    .get();
  return existingAccounts.docs.some((doc) => {
    const data = doc.data() as Partial<GeneralLedgerAccountV2>;
    if (data.deleted) return false;
    if (!data.name) return false;
    return normalizeAccountName(data.name) === normalizedName;
  });
}

export async function addGeneralLedgerAccount(
  accountName: string,
  accountType: GeneralLedgerAccountV2Type
): Promise<GeneralLedgerAccountV2> {
  const userId = await getUserId();
  const normalizedName = normalizeAccountName(accountName);
  if (!normalizedName?.trim()) {
    throw new Error("Account name cannot be empty.");
  }
  if (await generalLedgerAccountExists(accountName)) {
    throw new Error("A general ledger account with this name already exists.");
  }

  const ref = db.collection("GL_Accounts").doc();
  const value = {
    name: accountName.trim(),
    type: accountType,
    id: ref.id,
    user: userId,
  };
  await ref.set(value);

  return value;
}

export const updateGeneralLedgerAccount = async (accountId, value, field) => {
  const ref = db.collection("GL_Accounts").doc(accountId);
  let docData = await ref.get();
  let glAccount = docData.data();
  if (glAccount) {
    glAccount[field] = value;
    return await ref.set(glAccount, { merge: true });
  }
  return false;
};

export const deleteGeneralLedgerAccount = async (accountId) => {
  return await updateGeneralLedgerAccount(accountId, true, "deleted");
};
